import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  buildInviteMessage,
  emailLink,
  formatEndDate,
  inviteSubject,
  whatsappLink,
} from "utils/inviteMessage";

const useStyles = makeStyles((theme) => ({
  actions: {
    flexWrap: "wrap",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 3, 2),
  },
  note: { marginTop: theme.spacing(1) },
}));

// "Invite a Friend": shows the message that goes with the team's invitation
// link, ready to edit, with the ways to send it - WhatsApp first, then the
// phone's share sheet, copy, or email. `fetchInvite(newLink)` resolves with
// { url, expiresAt }; opening this reuses the team's current link (so the one
// sent earlier still works), and "Make a new link" replaces it on purpose.
export default function InviteDialog({ open, onClose, teamName, fetchInvite }) {
  const classes = useStyles();
  const [invite, setInvite] = React.useState(null); // { url, expiresAt }
  const [message, setMessage] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [copied, setCopied] = React.useState(false);
  const [confirmingNew, setConfirmingNew] = React.useState(false);

  const load = React.useCallback(
    (newLink) => {
      setLoading(true);
      setError("");
      setCopied(false);
      setConfirmingNew(false);
      return Promise.resolve(fetchInvite(newLink))
        .then((result) => {
          setInvite(result);
          setMessage(buildInviteMessage({ teamName, url: result.url, expiresAt: result.expiresAt }));
        })
        .catch(() => setError("Couldn't create the invitation link - try again in a moment."))
        .finally(() => setLoading(false));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchInvite, teamName]
  );

  React.useEffect(() => {
    if (open) load(false);
    else setInvite(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const linkMissing = invite && !message.includes(invite.url);
  const canSend = Boolean(invite) && !linkMissing && !loading;

  const copyMessage = () => {
    Promise.resolve(navigator.clipboard.writeText(message))
      .then(() => setCopied(true))
      .catch(() => setError("Couldn't copy - select the text and copy it by hand."));
  };

  const openWhatsApp = () => window.open(whatsappLink(message), "_blank", "noopener");
  const openEmail = () => {
    window.location.href = emailLink(inviteSubject(teamName), message);
  };
  const share = () => {
    navigator
      .share({ title: inviteSubject(teamName), text: message })
      .catch((err) => {
        // Closing the share sheet isn't a failure.
        if (err.name !== "AbortError") copyMessage();
      });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="invite-title">
      <DialogTitle id="invite-title">Invite someone to {teamName || "your team"}</DialogTitle>
      <DialogContent>
        {loading && !invite && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress color="secondary" />
          </Box>
        )}
        {error && (
          <Typography color="error" variant="body2" paragraph>
            {error}
          </Typography>
        )}
        {invite && (
          <>
            <TextField
              id="invite-message"
              label="Message"
              multiline
              minRows={10}
              fullWidth
              variant="outlined"
              value={message}
              onChange={(event) => {
                setMessage(event.target.value);
                setCopied(false);
              }}
              error={Boolean(linkMissing)}
              helperText={
                linkMissing
                  ? "The invitation link is missing from your message. Put it back, or reset the message."
                  : "Edit it however you like - just keep the link in. It doesn't name you, so it can be passed on."
              }
            />
            <Typography variant="caption" color="textSecondary" component="p" className={classes.note}>
              {`The link works until ${formatEndDate(invite.expiresAt)}. Anyone who gets it can join the team until then, so only send it to people you want on the team.`}
            </Typography>
            <Box mt={1}>
              {confirmingNew ? (
                <>
                  <Typography variant="caption" color="textSecondary">
                    The link you sent before will stop working.{" "}
                  </Typography>
                  <Button size="small" color="secondary" onClick={() => load(true)} disabled={loading}>
                    Make a new link
                  </Button>
                  <Button size="small" onClick={() => setConfirmingNew(false)}>
                    Keep the current one
                  </Button>
                </>
              ) : (
                <>
                  <Button size="small" onClick={() => setConfirmingNew(true)}>
                    Use a different link
                  </Button>
                  <Button
                    size="small"
                    onClick={() =>
                      setMessage(buildInviteMessage({ teamName, url: invite.url, expiresAt: invite.expiresAt }))
                    }
                  >
                    Reset message
                  </Button>
                </>
              )}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button onClick={onClose}>Close</Button>
        <Button onClick={openEmail} disabled={!canSend}>
          Email
        </Button>
        <Button onClick={copyMessage} disabled={!canSend}>
          {copied ? "Copied" : "Copy message"}
        </Button>
        {typeof navigator !== "undefined" && navigator.share && (
          <Button onClick={share} disabled={!canSend}>
            Share…
          </Button>
        )}
        <Button variant="contained" color="secondary" onClick={openWhatsApp} disabled={!canSend}>
          WhatsApp
        </Button>
      </DialogActions>
    </Dialog>
  );
}
